import Tippy from '@tippyjs/react';
import { noop } from 'lodash';
import React, {
  ComponentProps,
  type CSSProperties,
  type ElementType,
  memo,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Placement } from 'tippy.js';
import 'tippy.js/dist/tippy.css';

import { TIPPY_PRESETS as presets } from '../../constants/tippy';
import { useToggle } from '../../hooks/useToggle';
import { plugins } from './plugins';

type Position = 'absolute' | 'fixed';
interface Props {
  // Node to append popover to (e.g. document.body)
  appendTo?: Element | 'parent';
  /** Popover contents */
  children?: React.ReactNode;
  /** Popover presets */
  preset?: keyof typeof presets;
  /** If the popover can be interacted (i.e. supports pointer events) */
  isInteractive?: boolean;
  /** Controlled visibility state */
  isVisible?: boolean;
  /** Sets the position attribute */
  position?: Position;
  /** Popover trigger element */
  trigger?: React.ReactElement;
  /** Optional styles assigned on the trigger node */
  triggerStyle?: React.CSSProperties;
  /** Maximum tooltip width (limited configurations) */
  maxWidth?: number | string;
  /** Optional styles assigned on the container node */
  containerStyle?: React.CSSProperties;
  /** placement of the popover. */
  placement?: Placement;
  /** distance between the trigger and the Popover */
  offset?: [number, number];
  /** Will create a new DOMRect to be used as a reference for the Popover.
   * Can be used when you want don't have a trigger and want to display in a specific position.
   */
  referencePosition?: { x: number; y: number };
  /** Callback when dropdown is click-defocused */
  onClickOutside?: (event: MouseEvent) => void;
  /** When false, outside clicks only invoke `onClickOutside` without auto-hiding. */
  hideOnClick?: boolean;
  /** Callback when popover is hidden */
  onHide?: () => void;
  /** Callback when popover is shown */
  onShow?: () => void;
  /** Exposes the underlying Tippy instance (e.g. to call `.hide()` programmatically) */
  onInstanceCreate?: (instance: any) => void;
  /**
   * Root element wrapping the Tippy reference. Default `div` (block).
   * Use `span` inside running text so the trigger stays inline with surrounding copy.
   */
  wrapperAs?: ElementType;
}

export const Popover = memo(
  ({
    appendTo,
    children,
    isInteractive = true,
    isVisible,
    maxWidth = 900,
    offset,
    preset = 'popover',
    placement: placementProp,
    position = 'fixed',
    referencePosition,
    trigger,
    triggerStyle = {},
    containerStyle = {},
    hideOnClick = true,
    onClickOutside,
    onHide = noop,
    onShow = noop,
    onInstanceCreate,
    wrapperAs: Wrapper = 'div',
  }: Props) => {
    const ref = useRef<HTMLDivElement>(null);
    const popperInstanceRef = useRef<any>(null);
    const [_toggledValue, _toggle, hide, show] = useToggle();
    // `presets` is a module constant, so this is a stable reference per preset
    // and safe to depend on in the observer effect below.
    const popperConfig = useMemo(
      () => (presets[preset] as { popperConfig?: any }).popperConfig ?? {},
      [preset],
    );

    useEffect(() => {
      if (isVisible && ref.current) {
        ref.current.focus();
      }
    }, [isVisible]);

    /**
     * Updates the popper instance when the content of the popover changes.
     * This is necessary to ensure that the popover position is recalculated when the content changes dynamically.
     * For example, when the dropdown options are loaded asynchronously and the dropdown grows in height compared to when its first opened.
     */
    useEffect(() => {
      // only create observer when the popover has flip configuration, as the update won't have a point without it
      if (!ref.current || !isVisible || !popperConfig.flip) return;

      const observer = new MutationObserver(() => {
        popperInstanceRef.current.popperInstance.update();
      });

      observer.observe(ref.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      return () => observer.disconnect();
    }, [isVisible, popperConfig.flip]);

    const handleClickOutside = (_instance: unknown, event: MouseEvent) => {
      if (
        event.target instanceof Element &&
        event.target.closest('.eds-select--listbox')
      ) {
        return;
      }
      onClickOutside?.(event);
    };

    const handleHide = () => {
      // Tippy does not remove blur on interactive elements.  See https://github.com/atomiks/tippyjs/issues/72#issuecomment-313588293
      if (isInteractive && ref.current?.contains(document.activeElement)) {
        const activeElement = document.activeElement as HTMLElement;
        activeElement.blur();
      }

      hide();
      onHide?.();
    };

    const handleShow = () => {
      show();
      onShow?.();
    };

    const { className, tippyProps } = presets[preset];
    const placement: Placement | undefined =
      placementProp ?? (tippyProps as ComponentProps<typeof Tippy>).placement;

    const isInlineSpanWrapper =
      typeof Wrapper === 'string' && Wrapper === 'span';
    const rootStyle: CSSProperties = {
      ...(isInlineSpanWrapper ? { display: 'inline' } : {}),
      ...containerStyle,
    };
    const innerTriggerStyle: CSSProperties = {
      ...componentStyles.trigger,
      ...(isInlineSpanWrapper ? { display: 'inline' } : {}),
      ...triggerStyle,
    };

    return (
      <Wrapper style={rootStyle}>
        <Tippy
          {...tippyProps}
          trigger={isVisible === undefined ? tippyProps.trigger : undefined}
          interactive={isInteractive}
          animation={false}
          appendTo={appendTo}
          className={['legacy-eds-popover', className].join(' ')}
          content={
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            <div ref={ref} role="alertdialog" tabIndex={0}>
              {children}
            </div>
          }
          {...(isVisible === undefined ? { hideOnClick } : {})}
          maxWidth={maxWidth}
          offset={offset}
          popperOptions={getPopperOptions({
            position,
            flip: popperConfig.flip,
          })}
          visible={isVisible}
          plugins={plugins}
          onClickOutside={handleClickOutside}
          onHide={handleHide}
          onShow={handleShow}
          placement={placement}
          getReferenceClientRect={
            referencePosition
              ? () =>
                  new DOMRect(referencePosition.x, referencePosition.y, 0, 0)
              : undefined
          }
          onCreate={(instance) => {
            popperInstanceRef.current = instance;
            onInstanceCreate?.(instance);
          }}
        >
          <span style={innerTriggerStyle}>{trigger}</span>
        </Tippy>
      </Wrapper>
    );
  },
);

const getPopperOptions = (config: {
  position: Position;
  flip?: {
    name: string;
    enabled: boolean;
    options?: { fallbackPlacements: Placement[] };
  };
}) => ({
  strategy: config.position, // tippy does not expose PositioningStrategy type
  modifiers: [
    {
      name: 'applyStyles',
      options: {
        styles: {
          top: 20,
          left: 20,
          bottom: 20,
          right: 20,
        },
      },
    },
    ...(config.flip ? [config.flip] : []),
  ],
});

export const componentStyles = {
  trigger: {
    cursor: 'pointer',
    display: 'inline-flex',
  },
};
